import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class ProductHeroGallery extends Component {
  #abortController;
  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#bindSwipe();
    this.#showMedia(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#abortController?.abort();
    this.#bindSwipe();
    this.#showMedia(0);
  }

  selectMedia(event) {
    const control = event.target.closest('[data-gallery-index]');
    if (!control) return;
    this.#showMedia(Number(control.dataset.galleryIndex));
  }

  previousMedia() { this.#showMedia(this.#index - 1); }
  nextMedia() { this.#showMedia(this.#index + 1); }

  #showMedia(index) {
    const count = this.querySelectorAll('[data-gallery-media]').length;
    if (!count) return;
    this.#index = Math.max(0, Math.min(index, count - 1));
    const track = this.querySelector('[ref="track"]');
    if (track) track.style.transform = `translateX(${this.#index * -100}%)`;
    this.querySelectorAll('[data-gallery-index]').forEach((control) => {
      const selected = Number(control.dataset.galleryIndex) === this.#index;
      control.classList.toggle('is-selected', selected);
      control.setAttribute('aria-pressed', String(selected));
    });
    const prev = this.querySelector('.product-hero__arrow--prev');
    const next = this.querySelector('.product-hero__arrow--next');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index === count - 1;

    const counter = this.querySelector('.product-hero__counter');
    if (counter) counter.textContent = `Imagen ${this.#index + 1} de ${count}`;

    this.#syncDots();
  }

  #syncDots() {
    const strip = this.querySelector('.product-hero__dots');
    if (!strip) return;

    const overflow = strip.scrollWidth - strip.clientWidth > 1;
    strip.classList.toggle('is-scrollable', overflow);

    const active = strip.querySelector('.product-hero__dot.is-selected');
    if (!active || !overflow) return;

    const stripRect = strip.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const delta = activeRect.left + activeRect.width / 2 - (stripRect.left + stripRect.width / 2);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    strip.scrollTo({ left: strip.scrollLeft + delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #bindSwipe() {
    const viewport = this.querySelector('[ref="viewport"]');
    if (!viewport) return;
    this.#abortController = new AbortController();
    let startX = 0;
    let startY = 0;
    viewport.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      startY = event.clientY;
    }, { signal: this.#abortController.signal });
    viewport.addEventListener('pointerup', (event) => {
      if (event.target.closest('.product-hero__arrow')) return;
      const deltaX = event.clientX - startX;
      if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(event.clientY - startY)) {
        this.#showMedia(this.#index + (deltaX < 0 ? 1 : -1));
      }
    }, { signal: this.#abortController.signal });
    window.addEventListener('resize', () => this.#syncDots(), { signal: this.#abortController.signal });
  }
}

if (!customElements.get('product-hero-gallery')) customElements.define('product-hero-gallery', ProductHeroGallery);

class ProductHeroPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const isManualSaving = this.dataset.manual === 'true';
    const isManualPrice = this.dataset.manualPrice === 'true';

    // event.promise resolves with {detail:{html, productId, newProduct, resource}}
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newBlock = html.querySelector(`product-hero-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newBlock) return;

        if (!isManualPrice) {
          const newPrice = newBlock.querySelector('[data-price-current]');
          const currentPrice = this.querySelector('[data-price-current]');
          if (newPrice && currentPrice) {
            currentPrice.textContent = newPrice.textContent;
            if (newPrice.hasAttribute('hidden')) {
              currentPrice.setAttribute('hidden', '');
            } else {
              currentPrice.removeAttribute('hidden');
            }
          }

          const newCompare = newBlock.querySelector('[data-price-compare]');
          const currentCompare = this.querySelector('[data-price-compare]');
          if (newCompare && currentCompare) {
            const hasCompare = !newCompare.hasAttribute('hidden') && newCompare.textContent.trim() !== '';
            if (hasCompare) {
              currentCompare.textContent = newCompare.textContent;
              currentCompare.removeAttribute('hidden');
            } else {
              currentCompare.setAttribute('hidden', '');
            }
          }
        }

        if (isManualSaving || isManualPrice) return;

        const newSaving = newBlock.querySelector('[data-price-saving]');
        const currentSaving = this.querySelector('[data-price-saving]');
        if (!currentSaving) return;
        if (!newSaving || newSaving.hasAttribute('hidden') || newSaving.textContent.trim() === '') {
          currentSaving.setAttribute('hidden', '');
        } else {
          currentSaving.textContent = newSaving.textContent;
          currentSaving.removeAttribute('hidden');
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-hero-price] update failed:', error);
      });
  };
}

if (!customElements.get('product-hero-price')) customElements.define('product-hero-price', ProductHeroPrice);

class ProductHeroStockNotice extends Component {
  #count = 0;
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#restart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimer();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restart();
  }

  #restart() {
    this.#clearTimer();
    const [min, max] = this.#bounds();
    const initial = Number.parseInt(this.dataset.initialCount, 10);
    this.#count = Number.isFinite(initial) ? Math.min(max, Math.max(min, initial)) : Math.round((min + max) / 2);
    this.#render();
    this.#schedule();
  }

  #bounds() {
    const min = Math.max(1, Number.parseInt(this.dataset.countMin, 10) || 1);
    const max = Math.max(min, Number.parseInt(this.dataset.countMax, 10) || min);
    return [min, max];
  }

  #schedule() {
    const delay = 45000 + Math.floor(Math.random() * 45001);
    this.#timer = window.setTimeout(() => {
      this.#advance();
      this.#schedule();
    }, delay);
  }

  #advance() {
    const [min, max] = this.#bounds();
    if (min === max) return;
    const amount = 1 + Math.floor(Math.random() * 2);
    const direction = Math.random() < 0.5 ? -1 : 1;
    let next = Math.min(max, Math.max(min, this.#count + direction * amount));
    if (next === this.#count) next = Math.min(max, Math.max(min, this.#count - direction * amount));
    this.#count = next;
    this.#render(true);
  }

  #render(animate = false) {
    const text = this.querySelector('[data-stock-notice-text]');
    if (!text) return;
    text.textContent = (this.dataset.countTemplate || '').split('{count}').join(String(this.#count));
    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    text.classList.remove('is-updating');
    requestAnimationFrame(() => text.classList.add('is-updating'));
  }

  #clearTimer() {
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = undefined;
    }
  }
}

if (!customElements.get('product-hero-stock-notice')) customElements.define('product-hero-stock-notice', ProductHeroStockNotice);
